import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { formatDateDisplay } from '../../utils/dateUtils';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  Flex,
  Badge,
  useColorModeValue,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Link,
  TableContainer,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { useAuth } from '../../contexts/AuthContext';
import ticketService from '../../services/ticketService';
import DueDateDisplay from '../../components/DueDateDisplay';

const Dashboard = () => {
  const { user } = useAuth();
  const [ticketStats, setTicketStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
  });
  const [recentTickets, setRecentTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Get ticket stats based on user role
        const ticketStatsResponse = await ticketService.getTicketStats();

        // Get recent tickets - use either myTickets or allTickets endpoint based on role
        let ticketsResponse;
        if (user.role === 'admin') {
          // For admins, show most recent submitted tickets prioritized by urgency
          ticketsResponse = await ticketService.getTickets({
            limit: 5,
            sort: '-priority,dueDate,-createdAt'
          });
        } else {
          // For support/client, show their assigned/submitted tickets prioritized by urgency
          ticketsResponse = await ticketService.getMyTickets({
            limit: 5,
            sort: '-priority,dueDate,-createdAt'
          });
        }

        // Set stats
        const stats = ticketStatsResponse.data.data;
        setTicketStats(stats);

        // Set recent tickets
        const recent = ticketsResponse.data.data;
        setRecentTickets(recent);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [user.role]);

  // Helper function to get the appropriate badge color for ticket status
  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'blue';
      case 'in-progress':
        return 'yellow';
      case 'resolved':
        return 'green';
      case 'closed':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Helper function to get the appropriate badge color for ticket priority
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  // Get role-specific ticket stats context
  const getStatsContext = () => {
    switch (user.role) {
      case 'client':
        return {
          title: "Organization's Tickets",
          helpText: "Tickets in your organization"
        };
      case 'support':
        return {
          title: "Tickets in Your Projects",
          helpText: "Tickets created in your projects"
        };
      case 'admin':
        return {
          title: "All Tickets",
          helpText: "All tickets in the system"
        };
      default:
        return {
          title: "Tickets",
          helpText: "All tickets"
        };
    }
  };

  const statsContext = getStatsContext();

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">Welcome, {user?.name}</Heading>
          <Text color="gray.600">
            {user.role === 'client'
              ? "Here's an overview of your tickets and organization's tickets"
              : user.role === 'support'
                ? "Here's an overview of tickets belonging to your projects"
                : "Here's an overview of all tickets in the system"}
          </Text>
        </Box>
        <Button
          as={RouterLink}
          to="/tickets/new"
          colorScheme="brand"
          leftIcon={<AddIcon />}
        >
          Create New Ticket
        </Button>
      </Flex>

      {/* Ticket Statistics */}
      <SimpleGrid columns={{ base: 1, md: 5 }} spacing={6} mb={8}>
        <StatCard
          title={statsContext.title}
          value={ticketStats.total}
          helpText={statsContext.helpText}
          accentColor="brand.500"
        />
        <StatCard
          title="Open"
          value={ticketStats.open}
          helpText="Awaiting response"
          accentColor="blue.500"
        />
        <StatCard
          title="In Progress"
          value={ticketStats.inProgress}
          helpText="Being worked on"
          accentColor="yellow.500"
        />
        <StatCard
          title="Resolved"
          value={ticketStats.resolved}
          helpText="Solution provided"
          accentColor="green.500"
        />
        <StatCard
          title="Closed"
          value={ticketStats.closed}
          helpText="Completed"
          accentColor="gray.500"
        />
      </SimpleGrid>

      {/* Recent Tickets */}
      <Box
        bg={useColorModeValue('white', 'gray.700')}
        borderRadius="lg"
        boxShadow="sm"
        overflow="hidden"
      >
        <Flex
          bg={useColorModeValue('gray.50', 'gray.800')}
          p={4}
          justify="space-between"
          align="center"
        >
          <Box>
            <Heading size="md">
              {user.role === 'client' ? 'Your Recent Tickets' : 'Recent Assigned Tickets'}
            </Heading>
            <Text fontSize="sm" color="gray.600" mt={1}>
              {user.role === 'admin'
                ? 'Latest tickets from all organizations, prioritized by urgency and due date'
                : user.role === 'client'
                  ? 'Your submitted tickets, prioritized by urgency and due date'
                  : 'Tickets assigned to you, prioritized by urgency and due date'}
            </Text>
          </Box>
          <HStack>
            <Button
              as={RouterLink}
              to="/tickets"
              variant="ghost"
              colorScheme="brand"
              size="sm"
            >
              View All
            </Button>
          </HStack>
        </Flex>

        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Title</Th>
                <Th>Project</Th>
                <Th>Status</Th>
                <Th>Priority</Th>
                <Th>Created</Th>
                <Th>Due Date</Th>
                <Th>Resolved</Th>
              </Tr>
            </Thead>
            <Tbody>
              {recentTickets.length > 0 ? (
                recentTickets.map((ticket) => (
                  <Tr key={ticket._id}>
                    <Td maxW="300px" isTruncated>
                      <Link
                        as={RouterLink}
                        to={`/tickets/${ticket._id}`}
                        color="brand.600"
                        fontWeight="medium"
                      >
                        {ticket.title}
                      </Link>
                    </Td>
                    <Td>{ticket.project.name}</Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(ticket.status)}>
                        {ticket.status}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={getPriorityColor(ticket.priority)}>
                        {ticket.priority}
                      </Badge>
                    </Td>
                    <Td>
                      {formatDateDisplay(ticket.createdAt)}
                    </Td>
                    <Td>
                      <DueDateDisplay dueDate={ticket.dueDate} ticketStatus={ticket.status} />
                    </Td>
                    <Td>
                      {ticket.resolvedDate ? formatDateDisplay(ticket.resolvedDate) : '-'}
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={7} textAlign="center" py={4}>
                    <Text color="gray.500">
                      {user.role === 'client'
                        ? "No tickets found. Create your first ticket to get started."
                        : "No tickets are submitted."}
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

// Statistic Card Component
const StatCard = ({ title, value, helpText, accentColor }) => {
  return (
    <Box
      bg={useColorModeValue('white', 'gray.700')}
      p={4}
      borderRadius="lg"
      boxShadow="sm"
      borderLeft="4px solid"
      borderColor={accentColor}
    >
      <Stat>
        <StatLabel>{title}</StatLabel>
        <StatNumber fontSize="3xl">{value}</StatNumber>
        <StatHelpText>{helpText}</StatHelpText>
      </Stat>
    </Box>
  );
};

export default Dashboard;
